import classnames from "classnames";
import { useAppSelector } from "@web-solutions/store/types";

import React, { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { Text, Title } from "@web-solutions/core/ui-elements";

import { LString, t, tm, TM } from "@web-solutions/module-localization";

import { PaymentModalType } from "core/constants/remote-config";

import { useRemoteConfig } from "core/hooks/use-remote-config";
import { useTimer } from "core/payment/hooks/use-timer";
import { ProductDetails } from "core/store/billing/selectors";

import EmailEditor from "../../components/edit-email-field";

import { Prices } from "./components/prices";

import { SimpleView } from "./components/simple-view";
import { getActualPrices } from "./components/simple-view/utilts";
import { TrialInfoBlock } from "./components/trial-info-block";

import classes from "./style.module.scss";

const tKey = "core.payment_popup";

interface ProductInfoProps {
  product: ProductDetails;
  subtitle?: React.ReactNode;
  isSimpleView?: boolean;
  modalType?: PaymentModalType;
}

export const ProductInfo: React.FC<ProductInfoProps> = ({
  product,
  subtitle,
  isSimpleView,
  modalType,
}) => {
  const { title, textLineThrough, discount, isOneTimePurchase, isTrial } =
    product;

  const {
    paymentModalHeader,
    showPricesBlock,
    totalPriceType,
    paymentModalType,
    promocodeOffer,
    paymentModalEmailEnabled,
    simpleTitleModal,
    trialInfoBlock,
    paymentPopup,
    newPaymentModal,
    paymentSystem,
    collectEmailAfterPayment,
  } = useRemoteConfig();

  // Deferred-email flow (Stripe and Truegate): the email is collected after
  // payment on a dedicated page, so hide the email field inside the card form.
  const hideEmailField =
    collectEmailAfterPayment &&
    (paymentSystem === "stripe" || paymentSystem === "truegate");

  const promocodeActivated = useSelector(
    (state: any) => state.billing.promocodeActivated
  );

  const locale = useAppSelector((state) => state.locale.locale);

  const { vat, vatPrice, price, fullPrice, actualPrice } = getActualPrices({
    activeProduct: product,
    totalPriceType,
  });
  const titleText = product.title?.[locale] || "";

  const timer = useTimer();
  const isShownTimerBlock = !!discount && timer?.shown;

  const effectivePaymentModalType = modalType || paymentModalType;
  const isSimpleModal = effectivePaymentModalType === PaymentModalType.SIMPLE;
  const isSimpleTitleModal = effectivePaymentModalType === PaymentModalType.SIMPLE_TITLE;

  const paymentModalHeaderParams = { titleText, ...product };

  return (
    <div
      className={classnames(classes.wrap, { [classes.simple]: isSimpleView })}
    >
      {isOneTimePurchase ? (
        // One-time purchase (upsale offer): a header with what is being bought
        // and its price, separated from the payment methods below.
        <div className={classes.oneTimeHeader}>
          <p className={classes.oneTimeName}>
            {tm(title?.[locale as keyof LString], "")}
          </p>
          <p className={classes.oneTimePrice}>
            {!!textLineThrough && (
              <span className={classes.lineThrough}>
                {tm(textLineThrough, "", product)}
              </span>
            )}
            {price}
          </p>
        </div>
      ) : (
        <>
          {!(isSimpleModal || isSimpleTitleModal) ? (
            <>
              <div
                className={newPaymentModal ? classes.blockNew : classes.block}
              >
                <div className={classes.headerWrapper}>
                  {paymentModalHeader?.title && (
                    <Title level="h3" className={classes.modalTitle}>
                      <TM
                        k={paymentModalHeader.title}
                        tOptions={paymentModalHeaderParams}
                      />
                    </Title>
                  )}

                  {/* title */}
                  {newPaymentModal ? (
                    <p className={classes.modalSubtitle}>
                      <p className={classes.titleReservedNew}>
                        {t('onboarding.paymentModal.timer')} <Timer />
                      </p>
                    </p>
                  ) : (
                    <p className={classes.modalSubtitle}>
                      <p className={classes.titleReserved}>
                      {t('onboarding.paymentModal.yourDisctount')}{' '}<Timer />{' '}{t('onboarding.paymentModal.min')}
                      </p>
                    </p>
                  )}

                  {/* {tm(
                      paymentModalHeader?.subtitle,
                      paymentPopup.selectedTitles[locale],
                      paymentModalHeaderParams,
                    )} */}
                </div>
                {subtitle}
                {(promocodeActivated ||
                  (!promocodeOffer.enabled &&
                    promocodeOffer.switchDefaultValue)) && (
                  <div className={classes.promocode}>
                    <span>🎁</span>
                    {t(`${tKey}.promocode.text`)}
                  </div>
                )}
                {effectivePaymentModalType === PaymentModalType.BOTTOM_INJECTED &&
                  discount && (
                    <div className={classes.specialOffer}>
                      <div className={classes.title}>
                        {t(`${tKey}.modal_discount.title`)}
                      </div>
                      <div className={classes.text}>
                        {t(`${tKey}.modal_discount.text`, { discount })}
                      </div>
                    </div>
                  )}
                {isShownTimerBlock && !newPaymentModal && (
                  <div className={classes.row}>
                    <p className={classes.discountTitle}>
                      {t(
                        `${tKey}.${timer?.shown ? (discount ? "discount" : "discount_zero") : "discount_save"}`,
                        { discount }
                      )}
                    </p>
                    {timer?.shown && (
                      <div className={classes.discount}>{timer?.text}</div>
                    )}
                  </div>
                )}
              </div>

              {!newPaymentModal && <div className={classes.divider} />}

              {showPricesBlock && (
                <Prices product={product} priceText={price} />
              )}
            </>
          ) : (
            <>
              <SimpleView
                showPrice={!simpleTitleModal?.withTotalPriceUnder}
                fullPrice={fullPrice}
                actualPrice={actualPrice}
                discount={discount}
                vatPrice={vatPrice}
                vat={vat}
                discountPrice={product?.savedPrice || ""}
                mode={isSimpleTitleModal ? simpleTitleModal?.type : undefined}
              />
            </>
          )}
          {paymentModalEmailEnabled && !hideEmailField && (
            <>
              <div
                className={
                  newPaymentModal ? classes.dividerNew : classes.divider
                }
              />
              <EmailEditor isSimpleView={isSimpleView} />
            </>
          )}
          {trialInfoBlock.show && isTrial && (
            <TrialInfoBlock
              product={product}
              periodMode={trialInfoBlock.periodMode}
            />
          )}
        </>
      )}
    </div>
  );
};

export const Timer = () => {
  const [timeLeft, setTimeLeft] = useState(595);
  const [isTimerVisible, setIsTimerVisible] = useState(true);

  const { newPaymentModal } = useRemoteConfig();

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          setIsTimerVisible(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <>
      {newPaymentModal ? (
        <div className={classes.timerWrapper}>
          <span style={{ color: "#E768B0" }}>{formatTime(timeLeft)}</span>
        </div>
      ) : (
        <span style={{ color: "#E768B0" }}>{formatTime(timeLeft)}</span>
      )}
    </>
  );
};
